'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { buildUrl, formatCurrency, formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { BillingInvoice, BillingType } from './util'
import { useAuthClient, useSharedTRPC } from '../../context'
import {
  invoiceChargeTitle,
  invoicePeriod,
  invoiceRenewalBridge,
  invoiceSeatBreakdown,
  invoiceSeatLabel,
  invoiceStatusLabel,
} from './util'

interface InvoiceDetailDialogProps {
  invoice: BillingInvoice
  type: BillingType
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Checkout path to re-order an expired invoice of this kind. */
function reorderPath(invoice: BillingInvoice): string {
  if (invoice.type === 'ADD-ON') return '/checkout/add-on'
  if (invoice.type === 'RENEWAL') return '/checkout/perpanjang'
  return '/checkout/new'
}

export function InvoiceDetailDialog({ invoice, type, open, onOpenChange }: InvoiceDetailDialogProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()

  const { data: activeMemberRole } = authClient.useActiveMemberRole()

  // School teachers (role `member`) may read billing but never act on it: every action below turns
  // into an explaining tooltip for them. Family accounts have no such split.
  const isReadOnly = type === 'school' && activeMemberRole?.role === 'member'

  // Rendering the PDF is a server-side render per call, so it is fetched on click rather than on
  // open — a browsed-through history would otherwise render one PDF per row the user glances at.
  const pdfQuery = useQuery({
    ...trpc.invoice.getPdf.queryOptions({ invoiceId: invoice.id }),
    enabled: false,
    staleTime: 1000 * 60 * 60,
  })

  const receiptQuery = useQuery({
    ...trpc.invoice.getReceiptUrl.queryOptions({ invoiceId: invoice.id }),
    enabled: false,
  })

  const handleDownloadPdf = React.useCallback(async () => {
    const result = await pdfQuery.refetch()

    if (!result.data) {
      toast.error('Gagal mengunduh dokumen tagihan')
      return
    }

    const byteCharacters = atob(result.data.pdf)
    const byteArray = new Uint8Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i)
    }

    const url = URL.createObjectURL(new Blob([byteArray], { type: 'application/pdf' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `PiBo_${invoice.invoiceNumber}.pdf`
    anchor.click()
    URL.revokeObjectURL(url)
  }, [invoice.invoiceNumber, pdfQuery])

  const handleViewReceipt = React.useCallback(async () => {
    const result = await receiptQuery.refetch()

    if (result.data?.url) window.open(result.data.url, '_blank', 'noopener,noreferrer')
    else toast.error('Bukti pembayaran tidak tersedia')
  }, [receiptQuery])

  const period = invoicePeriod(invoice)
  const seatBreakdown = invoiceSeatBreakdown(invoice)
  const renewalBridge = invoiceRenewalBridge(invoice)
  const isUnpaid = invoice.status !== 'PAID'

  const documentButton = (
    <Button variant='outline' loading={pdfQuery.isFetching} disabled={pdfQuery.isFetching} onClick={handleDownloadPdf}>
      <Receipt className='srd:mr-2 srd:size-5' />
      {invoice.status === 'PAID' ? 'Tanda terima' : 'Tagihan'}
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:max-h-[95dvh] srd:max-w-[500px] srd:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Detail Tagihan</DialogTitle>

          <DialogDescription>
            No. {invoice.invoiceNumber}
            <br />
            Tanggal {formatDate(invoice.createdAt, { dateStyle: 'medium' })}
          </DialogDescription>
        </DialogHeader>

        <section className='srd:flex srd:flex-wrap srd:gap-x-8 srd:gap-y-2 srd:pt-2'>
          <div className='srd:flex srd:flex-col srd:gap-y-1'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Jatuh tempo</p>
            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
              {invoice.expiredAt ? formatDate(invoice.expiredAt, { dateStyle: 'medium' }) : '-'}
            </p>
          </div>

          <div className='srd:flex srd:flex-col srd:gap-y-1'>
            <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>Status</p>
            <p
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                isUnpaid ? 'srd:text-error' : 'srd:text-neutral-black',
              )}
            >
              {invoiceStatusLabel(invoice.status)}
            </p>
          </div>
        </section>

        <section className='srd:flex srd:flex-col srd:gap-y-3 srd:border-t srd:border-grey-20 srd:pt-4'>
          <div className='srd:flex srd:flex-col srd:gap-y-1'>
            <h3 className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-black')}>
              {invoiceChargeTitle(invoice, type)}
            </h3>

            {period && (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>Periode {period}</p>
            )}
          </div>

          <div className='srd:flex srd:items-start srd:justify-between srd:gap-x-4'>
            <div className='srd:flex srd:flex-col srd:gap-y-0.5'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                {invoiceSeatLabel(invoice)}
              </p>

              {seatBreakdown && (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>{seatBreakdown}</p>
              )}
            </div>

            <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
              {formatCurrency(invoice.totalPrice - renewalBridge)}
            </p>
          </div>

          {/* A renewal's empty seats start at payment, not at the handover, and the days in
              between are charged pro-rata on top of the term. Its own line, or the seat line's
              "per kursi" and the figure beside it would not multiply out. */}
          {renewalBridge > 0 && (
            <div className='srd:flex srd:items-start srd:justify-between srd:gap-x-4'>
              <div className='srd:flex srd:flex-col srd:gap-y-0.5'>
                <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                  Kursi kosong aktif lebih awal
                </p>
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  Pro-rata sejak pembayaran sampai periode berjalan berakhir
                </p>
              </div>

              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                {formatCurrency(renewalBridge)}
              </p>
            </div>
          )}

          {!!invoice.totalDiscount && invoice.totalDiscount > 0 && (
            <div className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:border-t srd:border-grey-20 srd:pt-3'>
              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>Diskon promo</p>

              <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
                ({formatCurrency(invoice.totalDiscount)})
              </p>
            </div>
          )}

          <div className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:border-t srd:border-grey-20 srd:pt-3'>
            <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>Total</p>

            <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
              {formatCurrency(invoice.amountPaid)}
            </p>
          </div>
        </section>

        {invoice.status === 'PENDING' && (
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
            Pesanan batal otomatis jika tidak dibayar sampai tanggal jatuh tempo.
          </p>
        )}

        <footer className='srd:flex srd:flex-col-reverse srd:gap-2 srd:sm:flex-row srd:sm:justify-end srd:mt-4 srd:flex-wrap'>
          {isReadOnly ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button baseButton variant='outline' className='srd:text-nowrap'>
                    <Receipt className='srd:mr-2 srd:size-5' />
                    {invoice.status === 'PAID' ? 'Tanda terima' : 'Tagihan'}
                  </Button>
                }
              />

              <TooltipContent>
                <p>Silakan hubungi admin untuk mendapatkan dokumen tagihan</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            documentButton
          )}

          {invoice.status === 'PAID' && (
            <Button className='srd:text-nowrap' onClick={() => onOpenChange(false)}>
              Kembali
            </Button>
          )}

          {invoice.status === 'PENDING' &&
            (isReadOnly ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button baseButton variant='gradient' className='srd:text-nowrap'>
                      Bayar segera
                    </Button>
                  }
                />

                <TooltipContent>
                  <p>Silakan hubungi admin untuk melakukan pembayaran</p>
                </TooltipContent>
              </Tooltip>
            ) : invoice.paymentUrl ? (
              <Button asChild variant='gradient' className='srd:text-nowrap'>
                <a href={invoice.paymentUrl}>Bayar segera</a>
              </Button>
            ) : (
              <Button
                variant='gradient'
                className='srd:text-nowrap'
                onClick={() => toast.error('Tidak ditemukan URL pembayaran.')}
              >
                Bayar segera
              </Button>
            ))}

          {invoice.status === 'EXPIRED' &&
            (isReadOnly || invoice.type === 'DIRECT_PAID' ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button baseButton className='srd:text-nowrap'>
                      Pesan lagi
                    </Button>
                  }
                />

                <TooltipContent>
                  <p>Silakan hubungi admin untuk memesan kembali</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button asChild className='srd:text-nowrap'>
                <a
                  href={buildUrl(type, reorderPath(invoice), { premium_seats: invoice.seats.toString() }).href}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Pesan lagi
                </a>
              </Button>
            ))}

          {invoice.type === 'DIRECT_PAID' && invoice.status === 'PAID' && invoice.receiptFileId && !isReadOnly && (
            <Button
              variant='outline'
              className='srd:text-nowrap'
              loading={receiptQuery.isFetching}
              disabled={receiptQuery.isFetching}
              onClick={handleViewReceipt}
            >
              <Receipt className='srd:mr-2 srd:size-5' />
              Bukti pembayaran
            </Button>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  )
}
